import type { FC } from 'react';

import { memo, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

import { media } from '~/components/shared/core/styles';
import { SiteType } from '~/utilities/graphql/codegen';
import type { Signing } from '~/utilities/graphql/codegen';

const SigningContainerStyles: Record<SiteType, ReturnType<typeof css>> = {
  [SiteType.Gstar]: css``,
  [SiteType.Aaf]: css``,
  [SiteType.Employeeshop]: css``,
  [SiteType.Outlet]: css``,
  [SiteType.Wahts]: css`
    --signing-background-color: ${({ theme }) => theme.colors.background.component.shared.sale};

    @media ${({ theme }) => media(theme).lessThan('sm')} {
      min-height: 14px;
    }
  `,
};

const SigningLabelStyles: Record<SiteType, ReturnType<typeof css>> = {
  [SiteType.Gstar]: css``,
  [SiteType.Aaf]: css``,
  [SiteType.Employeeshop]: css``,
  [SiteType.Outlet]: css`
    &.discount-signing {
      --signing-text-color: ${({ theme }) => theme.colors.neutral[0]};
    }
  `,
  [SiteType.Wahts]: css`
    --signing-text-color: ${({ theme }) => theme.colors.content.primary};

    line-height: 1.4;

    @media ${({ theme }) => media(theme).lessThan('sm')} {
      font-size: ${({ theme }) => theme.font.size[1]};
    }

    &.discount-signing {
      --signing-text-color: ${({ theme }) => theme.colors.content.primary};
    }
  `,
};

export const S = {
  Signing: styled.div`
    ${({ theme }) => css`
      --signing-background-color: ${theme.colors.neutral['0-70']};

      &.discount-signing {
        --signing-background-color: ${theme.colors.background.component.shared.sale};
      }

      background-color: var(--signing-background-color);
      text-align: center;
      position: relative;
      min-height: 16px;
      width: fit-content;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0 4px;
      z-index: 21;
      white-space: nowrap;
      border-radius: ${theme.borders.radius.xs};
      cursor: default;

      ${SigningContainerStyles[theme.siteType]}
    `}
  `,
  SigningLabel: styled.span`
    ${({ theme }) => css`
      --signing-text-color: ${theme.colors.neutral[1000]};

      &.discount-signing {
        --signing-text-color: ${theme.colors.content.inverse};
      }

      color: var(--signing-text-color);
      display: block;
      font-weight: ${theme.variables.font.weight.medium};
      left: 50%;
      top: 50%;
      transform: unset;
      position: unset;
      letter-spacing: normal;
      line-height: 16px;
      font-size: ${theme.font.size[2]};
      width: 100%;

      ${SigningLabelStyles[theme.siteType]}
    `}
  `,
};

interface Props {
  signing: Signing;
}

export const TextSigningComponent: FC<Props> = ({ signing }) => {
  const {
    discountSigning,
    productSigningTextColour,
    productSigningBackgroundColour,
    labelCssClasses,
    label,
  } = signing;

  const signingColors = useMemo(
    () => ({
      ...(productSigningBackgroundColour && {
        '--signing-background-color': productSigningBackgroundColour,
      }),
      ...(productSigningTextColour && { '--signing-text-color': productSigningTextColour }),
    }),
    [productSigningBackgroundColour, productSigningTextColour]
  );

  const signingClassName = discountSigning ? 'discount-signing' : '';

  return (
    <S.Signing
      data-testid={`${discountSigning ? 'discount-signing' : 'text-signing'}`}
      className={signingClassName}
      style={signingColors}
    >
      <S.SigningLabel className={`${labelCssClasses} ${signingClassName}`} style={signingColors}>
        {label}
      </S.SigningLabel>
    </S.Signing>
  );
};

export const TextSigning = memo(TextSigningComponent, isEqual);
